import React, { useEffect, useMemo, useState } from "react";
import { pubCall } from "../utils/PubSub";
import { showError, showMessage } from "../ui/Message";
import VirtualTable from "../ui/VirtualTable";
import { useTabs } from "../hooks/useTabs";
import Input from "../ui/Input";
import { KBD } from "../utils/Keybindings";
import { listen } from "../hooks/Listener";
import Spin from "../ui/Spin";
import EnumField from "../ui/EnumField";
import Space from "../ui/Space";
import { drawPicker } from "../popovers/Picker";
import { getEnumLabel, kbdEvent } from "../utils/Utils";
import Status from "../ui/Status";
import { InfoIcon } from "../utils/Icons";
import { ROUTES } from "../utils/Routes";
import CustomersController from "../controllers/CustomersController";
import { createScreen } from "../hooks/createScreen";
import { getSignal } from "../App";

export default function SetupPOS(props) {
    const { controller = "", route = "", callback = () => { } } = props;

    const [settings, setSettings] = useState({});
    const [initSettings, setInitSettings] = useState({});
    const [selected, setSelected] = useState({});
    const [fieldMode, setFieldMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editSnapshot, setEditSnapshot] = useState({});
    const row_key = "key";




    const columns = [
        { key: "letter", title: <></>, align: "center", width: 25 },
        { key: "display", title: <></>, align: "left", width: 100, grow: true },
    ];

    const { tabs, setTabs, index, help, update, setIndex, next, prev, refs, getVal } = useTabs([], "");


    const editProps = (key, save_on_change = false, proceed_on_change = true) => {
        return {
            ref: (el) => {
                refs.current[key] = el;
            },
            value: settings?.[key] ?? "",
            onChange: (value) => {
                update(key, value);

                setSettings((prev) => ({
                    ...prev,
                    [key]: value,
                }));
                if (save_on_change) {
                    save();
                } else {
                    if (proceed_on_change) {
                        next();
                    }

                }

            },
            onEnter: () => next(),
            onPageDown: () => save(),
            onPageUp: () => prev(),
        };
    };

    const getSettings = () => {
        setLoading(true)
        pubCall("setting.point_of_sale.getSettings", {}, (_s, _r) => {
            if (_s) {
                setSettings(_r || {});
                setInitSettings(_r || {});
            } else {
                showError(_r);
            }
            setLoading(false)
        });
    };

    useEffect(getSettings, []);



    const getTenderOptions = (callback = () => { }) => {
        pubCall("setting.point_of_sale.getDefaultTenderOptions", {}, (_s, _r) => {
            if (_s) {
                drawPicker({
                    controller: controller,
                    title: "Select Tender Option", closeable: true, height: 300, route: route, callback: (e) => {
                        let _default_tender = _r.find(item => item.number === e);
                        callback(_default_tender);
                    }, offset: [0, 10],
                    options: _r.map(item => {
                        return {
                            label: item.name,
                            value: item.number
                        }
                    })
                })
            } else {
                showError(_r)
            }
        })
    }


    const rows = useMemo(() => {
        return [
            {
                key: "sales_started_in_field",
                letter: "A",
                fields: [
                    {
                        key: "sales_started_in_field",
                        value: settings?.sales_started_in_field || "",
                        skip: false,
                    }
                ],
            },
            {
                key: "default_tender",
                letter: "B",
                fields: [],
                action: (e) => getTenderOptions((e) => {
                    const nextSettings = { ...settings, default_tender: e };
                    setSettings(nextSettings);
                    save({ settingsToSave: nextSettings });
                }),
            },
            {
                key: "default_tender_customer",
                letter: "C",
                fields: [],
                action: () => getTenderOptions((e) => {
                    const nextSettings = { ...settings, default_tender_customer: e };
                    setSettings(nextSettings);
                    save({ settingsToSave: nextSettings });
                }),
            },
            {
                key: "default_customer_linked_document_format",
                letter: "D",
                fields: [
                    {
                        key: "default_customer_linked_document_format",
                        value: settings?.default_customer_linked_document_format || "",
                        skip: false,
                    },
                    {
                        key: "default_customer_linked_print_type",
                        value: settings?.default_customer_linked_print_type || "",
                        skip: false,
                    }
                ],
            },

            {
                key: "default_customer_linked_on_acct_document_format",
                letter: "E",
                fields: [
                    {
                        key: "default_customer_linked_on_acct_document_format",
                        value: settings?.default_customer_linked_on_acct_document_format || "",
                        skip: false,
                    },
                    {
                        key: "default_customer_linked_on_acct_print_type",
                        value: settings?.default_customer_linked_on_acct_print_type || "",
                        skip: false,
                    }
                ],
            },
            {
                key: "warn_customer_sold_duplicate",
                letter: "F",
                fields: [],
                action: () => {
                    const nextSettings = { ...settings, warn_customer_sold_duplicate: !settings.warn_customer_sold_duplicate };
                    setSettings(nextSettings);
                    save({ settingsToSave: nextSettings });
                },
            },

            {
                key: "station_group",
                letter: "G",
                fields: [
                    {
                        key: "station_group",
                        value: settings?.station_group || "",
                        skip: false,
                    },
                    {
                        key: "station_edit_other_groups",
                        value: settings?.station_edit_other_groups || false,
                        skip: false,
                    }
                ],

            },

            {
                key: "offsite_group",
                letter: "H",
                fields: [
                    {
                        key: "offsite_group",
                        value: settings?.offsite_group || "",
                        skip: false,
                    }
                ],

            },

            {
                key: "offsite_customer",
                letter: "I",
                fields: [
                    {
                        key: "offsite_customer",
                        value: settings?.offsite_customer || "",
                        skip: false,
                    }
                ],

            },

            {
                key: "offsite_z_clerk",
                letter: "J",
                fields: [],
                action: () => {
                    const nextSettings = { ...settings, offsite_z_clerk: !settings.offsite_z_clerk };
                    setSettings(nextSettings);
                    save({ settingsToSave: nextSettings });
                },
            },
        ];
    }, [settings]);




    const statuses = {
        sales_started_in_field: "Options are: [I]SBN, [S]hipto",
        default_customer_linked_document_format: "[I]nvoice, [R]eceipt",
        default_customer_linked_print_type: "[P]rint, [E]mail",
        default_customer_linked_on_acct_document_format: "[I]nvoice, [R]eceipt",
        default_customer_linked_on_acct_print_type: "[P]rint, [E]mail",
        station_group: "Stations can be grouped by # for separate end-of-day totalling (default 00)",
        station_edit_other_groups: "This Station cannot edit other Group's sales unless this is Yes",
        offsite_group: "Use a group number other than 00 to keep offsite sales separate",
        offsite_customer: "Save time if the same customer account is used for each sale",
    }

    useEffect(() => {
        if (!selected?.key && rows.length) {
            setSelected(rows[0]);
        }
    }, [rows, selected?.key]);

    const valuesEqual = (a, b) => {
        return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    };

    const getChangedSettings = () => {
        const changed = {};

        Object.keys(settings || {}).forEach((key) => {
            if (!valuesEqual(settings[key], initSettings[key])) {
                changed[key] = settings[key];
            }
        });

        return changed;
    };

    const save = ({
        settingsToSave = settings,
        runCallback = false,
        exit = true
    } = {}) => {
        const changed = {};

        Object.keys(settingsToSave || {}).forEach((key) => {
            if (!valuesEqual(settingsToSave[key], initSettings[key])) {
                changed[key] = settingsToSave[key];
            }
        });

        if (!Object.keys(changed).length) {
            if (exit) {
                exitFieldMode();
            }

            if (runCallback) {
                callback();
            }

            return;
        }

        setLoading(true);

        pubCall("setting.point_of_sale.editSettings", {
            settings_in: { ...settingsToSave }
        }, (_s, _r) => {
            if (_s) {
                setInitSettings(settingsToSave);

                if (exit) {
                    exitFieldMode();
                }

                if (runCallback) {

                    callback();
                }
            } else {
                showError(_r);
            }

            setLoading(false);
        });
    };

    const blurField = (_field = index) => {
        const el = refs.current[_field];

        if (el && typeof el.blur === "function") {
            el.blur();
        }
    };

    const focusField = (_field = index) => {
        const el = refs.current[_field];

        if (el && typeof el.focus === "function") {
            el.focus();
        }
    };

    const rowNext = () => {
        if (fieldMode) {
            return;
        }

        const i = rows.findIndex((r) => r.key === selected?.key);
        const nextRow = rows[Math.min(i + 1, rows.length - 1)];

        if (nextRow) {
            setSelected(nextRow);
        }
    };

    const rowPrev = () => {
        if (fieldMode) {
            return;
        }

        const i = rows.findIndex((r) => r.key === selected?.key);
        const prevRow = rows[Math.max(i - 1, 0)];

        if (prevRow) {
            setSelected(prevRow);
        }
    };

    const enterRow = (_key = selected?.key) => {
        const row = rows.find((item) => item.key === _key);

        if (!row) {
            return;
        }

        if (!row.fields?.length) {
            row.action?.();
            return;
        }

        setTabs(row.fields);
        setIndex(row.fields[0].key);
        setFieldMode(true);

        requestAnimationFrame(() => {
            focusField(row.fields[0].key);
        });
    };

    const exitFieldMode = () => {
        blurField(index);
        setFieldMode(false);
    };

    const renderInput = (key, width = 40) => {
        return (
            <Input
                {...editProps(key)}
                width={width}
                edit={fieldMode && selected?.fields?.some((f) => f.key === key)}
                disabled={!fieldMode}
            />
        );
    };

    const linked_doc_options = [{ value: "I", key: KBD.I, label: "ISBN" }, { value: "S", key: KBD.S, label: "Shipto" }];
    const format_options = [{ value: "I", key: KBD.I, label: "Invoice" }, { value: "R", key: KBD.R, label: "Receipt" }];
    const print_options = [{ value: "P", key: KBD.P, label: "Print" }, { value: "E", key: KBD.E, label: "Email" }];
    const yes_no = [{ value: true, key: KBD.Y, label: "Yes" }, { value: false, key: KBD.N, label: "No" }];

    const rowEditing = (row) => {
        return fieldMode && selected?.letter === row.letter;
    }


    const selectCustomer = (_search = "") => {
        window.bm_state.value = ROUTES.CUSTOMER.CUSTOMER;
        blurField();

        createScreen({
            signal: getSignal(),
            component: (
                <CustomersController
                    controller={controller}
                    is_modal
                    search={_search}
                    route={ROUTES.CUSTOMER.CUSTOMER}
                />
            ),
            closable: false,
            title: "Select Customer",
            callback: (_obj) => {
                window.bm_state.value = route;

                if (!_obj) {
                    focusField();
                    return;
                }

                const shipTo = _obj.ship_to ?? "";

                update("offsite_customer", shipTo);

                setSettings((prev) => {
                    const nextSettings = {
                        ...prev,
                        offsite_customer: shipTo,
                    };

                    save({
                        settingsToSave: nextSettings,
                        exit: true,
                    });

                    return nextSettings;
                });

                exitFieldMode();
            },
        });
    };

    const data = rows.map((row) => {
        if (row.key === "sales_started_in_field") {

            return {
                ...row,
                display: (
                    <div>
                        Sales are started with cursor in the
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<EnumField className={"fill"} style={{ "height": "21px" }} width={60} {...editProps("sales_started_in_field", true)} edit={fieldMode && selected?.fields?.some((f) => f.key === "sales_started_in_field")} onEnter={() => kbdEvent(KBD.PAGE_DOWN)} options={linked_doc_options} /></span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}> {getEnumLabel(linked_doc_options, settings?.sales_started_in_field)}</span>)}
                    </div>
                ),
            };
        }
        if (row.key === "default_tender") {

            let _tender = getVal("default_tender");
            return {
                ...row,
                display: (
                    <div>
                        Sales (not linked to a customer) default to tender <span style={{ "color": "var(--text-yellow)" }}>{settings?.default_tender?.name || ""}</span>
                    </div>
                ),
            };
        }
        if (row.key === "default_tender_customer") {
            return {
                ...row,
                display: (
                    <div>
                        Sales linked to a customer default to tender <span style={{ "color": "var(--text-yellow)" }}>{settings?.default_tender_customer?.name || ""}</span>
                    </div>
                ),
            };
        }
        if (row.key === "default_customer_linked_document_format") {
            return {
                ...row,
                display: (
                    <div>
                        Customer linked-sales will default to
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<EnumField className={"fill"} style={{ "height": "21px" }} width={65} {...editProps("default_customer_linked_document_format")} edit={fieldMode && selected?.fields?.some((f) => f.key === "default_customer_linked_document_format")} options={format_options} />&nbsp;</span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}> {getEnumLabel(format_options, settings?.default_customer_linked_document_format)}</span>)}
                        &nbsp;format and
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<EnumField className={"fill"} style={{ "height": "21px" }} width={50} {...editProps("default_customer_linked_print_type", true)} edit={fieldMode && selected?.fields?.some((f) => f.key === "default_customer_linked_print_type")} onEnter={() => kbdEvent(KBD.PAGE_DOWN)} options={print_options} /></span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}> {getEnumLabel(print_options, settings?.default_customer_linked_print_type)}</span>)}
                    </div>
                ),
            };
        }
        if (row.key === "default_customer_linked_on_acct_document_format") {
            return {
                ...row,
                display: (
                    <div>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;or if OnAcct will default to
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<EnumField className={"fill"} style={{ "height": "21px" }} width={60} {...editProps("default_customer_linked_on_acct_document_format")} edit={fieldMode && selected?.fields?.some((f) => f.key === "default_customer_linked_on_acct_document_format")} options={format_options} />&nbsp;</span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}> {getEnumLabel(format_options, settings?.default_customer_linked_on_acct_document_format)}</span>)}
                        &nbsp;format and
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<EnumField className={"fill"} style={{ "height": "21px" }} width={60} {...editProps("default_customer_linked_on_acct_print_type", true)} edit={fieldMode && selected?.fields?.some((f) => f.key === "default_customer_linked_on_acct_print_type")} onEnter={() => kbdEvent(KBD.PAGE_DOWN)} options={print_options} /></span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}> {getEnumLabel(print_options, settings?.default_customer_linked_on_acct_print_type)}</span>)}
                    </div>
                ),
            };
        }
        if (row.key === "warn_customer_sold_duplicate") {
            return {
                ...row,
                display: (
                    <div>
                        Warn when customer sold a duplicate ISBN: <span style={{ "color": "var(--text-yellow)" }}>{(settings?.warn_customer_sold_duplicate) ? "Yes" : "No"}</span>
                    </div>
                ),
            };
        }

        if (row.key === "station_group") {
            return {
                ...row,
                display: (
                    <div>
                        Station {window.credentials.work_station_id} sales are with Group#
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<Input className={"fill"} maxLength={2} uppercase style={{ "height": "21px" }} width={35} {...editProps("station_group", false, false)} onFill={(e) => {
                            update("station_group", e);

                            setSettings((prev) => ({
                                ...prev,
                                ["station_group"]: e,
                            }));
                            next();
                        }} edit={fieldMode && selected?.fields?.some((f) => f.key === "station_group")} onEnter={(e) => {
                            next();
                        }} />&nbsp;</span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}> {settings?.station_group}</span>)}
                        &nbsp;Allow editing other Groups?
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<EnumField className={"fill"} style={{ "height": "21px" }} width={40} {...editProps("station_edit_other_groups", true, false)} edit={fieldMode && selected?.fields?.some((f) => f.key === "station_edit_other_groups")} onEnter={() => kbdEvent(KBD.PAGE_DOWN)} options={yes_no} /></span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}> {getEnumLabel(yes_no, settings?.station_edit_other_groups)}</span>)}
                    </div>
                ),
            };
        }

        if (row.key === "offsite_group") {

            return {
                ...row,
                display: (
                    <div>
                        Sales are recorded with Group #
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<Input uppercase maxLength={2} className={"fill"} style={{ "height": "21px" }} width={35} {...editProps("offsite_group")} edit={fieldMode && selected?.fields?.some((f) => f.key === "offsite_group")} onEnter={() => kbdEvent(KBD.PAGE_DOWN)} onFill={(e) => {
                            update("station_group", e);

                            setSettings((prev) => {
                                const nextSettings = {
                                    ...prev,
                                    "offsite_group": e,
                                };

                                save({
                                    settingsToSave: nextSettings,
                                    exit: true,
                                });

                                return nextSettings;
                            });
                        }} />&nbsp;&nbsp;</span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}>{settings?.offsite_group}&nbsp;</span>)}
                        (separating offsite sales is important)
                    </div>
                ),
            };
        }

        if (row.key === "offsite_customer") {

            return {
                ...row,
                display: (
                    <div>
                        Default customer acct for new sales
                        {(rowEditing(row) && <span style={{ display: "inline-block" }}>&nbsp;&nbsp;<Input uppercase className={"fill"} style={{ "height": "21px" }} width={275} {...editProps("offsite_customer")} onEnter={(e) => {
                            selectCustomer(e)
                        }} edit={fieldMode && selected?.fields?.some((f) => f.key === "offsite_customer")} />&nbsp;</span>)}
                        {(!rowEditing(row) && <span style={{ "color": "var(--text-yellow)" }}>&nbsp;{(settings?.offsite_customer) ? settings.offsite_customer : "No Customer"}</span>)}

                    </div>
                ),
            };
        }

        if (row.key === "offsite_z_clerk") {
            return {
                ...row,
                display: (
                    <div>
                        Use Clerk Z for all sales? <span style={{ "color": "var(--text-yellow)" }}>{(settings?.offsite_z_clerk) ? "Yes" : "No"}</span>
                    </div>
                ),
            };
        }

        return row;
    });

    const actions = [

        {
            key: KBD.ANY,
            action: (e) => {


                if (e.key === KBD.ESCAPE.key) {
                    if (fieldMode) {
                        exitFieldMode();
                        return;
                    }

                    callback();
                    return;
                }

                if (e.key === KBD.ENTER.key) {
                    if (!fieldMode) {
                        enterRow();
                        return;
                    }

                    next();
                    return;
                }

                if (e.key === KBD.ARROW_DOWN.key) {

                    if (fieldMode) {
                        next();
                        return;
                    }

                    rowNext();
                    return;
                }

                if (e.key === KBD.ARROW_UP.key) {
                    if (fieldMode) {
                        prev();
                        return;
                    }

                    rowPrev();
                    return;
                }

                if (e.key === KBD.PAGE_DOWN.key) {

                    if (fieldMode) {
                        save({ runCallback: false });
                        return;
                    } else {
                        save({ runCallback: true });
                    }
                    return;
                }


                if (!fieldMode) {


                    let _k = e.key.toLowerCase();

                    const row = rows.find(
                        (item) => item.letter?.toLowerCase() === _k
                    );
                    if (row) {
                        setSelected(row);
                        //enterRow(row?.key);
                        return;
                    }

                }

            },
        },




    ];

    useEffect(() => {
        const cleanup = listen(
            controller,
            route,
            actions,
            false,
            [

                KBD.ENTER,
                KBD.ARROW_DOWN,
                KBD.ARROW_UP,
                KBD.PAGE_DOWN,
                KBD.PAGE_UP,
                KBD.ESCAPE,
                KBD.MINUS,

            ]
        );

        return cleanup;
    }, [controller, route, selected, fieldMode, index, tabs, settings]);


    useEffect(() => {
        if (fieldMode) {
            refs?.current?.[index]?.focus?.();
            refs?.current?.[index]?.select?.();
        }
    }, [index, fieldMode])



    return (
        <div style={{ "width": "680px" }}>
            <Spin>
                <div className="shim" /><div className="shim" />
                <VirtualTable
                    no_header_borders={true}
                    showColumnHeaders={false}
                    controller={controller}
                    onRowClick={(item) => {
                        if (fieldMode) {
                            return;
                        }

                        setSelected(item);
                    }}
                    next={rowNext}
                    prev={rowPrev}
                    align={"bottom"}
                    row_key={row_key}
                    current={selected}
                    data={data}
                    height={255}
                    columns={columns}
                    width={0}
                    offset={830}
                    route={route}
                    scrollbar={true}
                    scrollindicator={true}
                    infinite={true}
                />

            </Spin>
            <div className="shim" /><div className="shim" /><div className="shim" /><div className="shim" />
            <div style={{ "backgroundColor": "var(--console-bg)", "margin": "-10px -10px", "borderTop": "2px solid var(--border-secondary)", "padding": "10px", "borderBottomLeftRadius": "5px", "borderBottomRightRadius": "5px" }}>
                <Status><InfoIcon /> {(fieldMode) ? statuses[index] : "Highlight option and press <enter> to change."}</Status>
            </div>
        </div>

    );
}